import React from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { pathToDroppableId } from './utils/pathHelpers';
import ElementRenderer from './ElementRenderer';
import { Box, GripVertical, Trash2 } from 'lucide-react';
import './styles/Canvas.css';

/**
 * Recursive Canvas component that renders elements and nested drop zones
 *
 * @param {Array} elements - Array of elements to render
 * @param {Array} path - Current path in the tree (for nested structures)
 * @param {Function} onElementSelect - Callback when element is selected
 * @param {String} selectedElementId - ID of currently selected element
 * @param {Boolean} isEditorMode - Whether in editor mode (shows visual aids)
 */
const Canvas = ({
  elements = [],
  path = [],
  onElementSelect,
  selectedElementId,
  isEditorMode = true,
  onElementDelete,
  parentType // NEW: to detect if parent is a grid-container
}) => {
  const droppableId = pathToDroppableId(path);
  const isRootCanvas = path.length === 0;
  const isInsideGridContainer = parentType === 'grid-container';

  // Disable root canvas as droppable if it already has a grid-container
  const hasGridContainer = isRootCanvas && elements.some(el => el.type === 'grid-container');
  const isDropDisabled = hasGridContainer;

  return (
    <Droppable droppableId={droppableId} isDropDisabled={isDropDisabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`
            canvas-drop-zone
            ${isRootCanvas ? 'root-canvas' : ''}
            ${isInsideGridContainer ? 'grid-container-canvas' : ''}
            ${snapshot.isDraggingOver ? 'dragging-over' : ''}
            ${elements.length === 0 ? 'empty' : ''}
            ${isEditorMode ? 'builder-editor-mode' : ''}
          `}
          data-path={droppableId}
        >
          {elements.length === 0 && isEditorMode && (
            <div className="canvas-empty-placeholder">
              <div className="placeholder-icon"><Box size={24} /></div>
              <div className="placeholder-text">
                {path.length === 0
                  ? 'Drag elements here to start building'
                  : 'Drop elements here'}
              </div>
            </div>
          )}

          {elements.map((element, index) => {
            // Grid-container is NOT draggable - it's fixed in place once dropped
            if (element.type === 'grid-container') {
              return (
                <CanvasElement
                  key={element.id}
                  element={element}
                  path={[...path, element.id]}
                  dragProvided={null}
                  isDragging={false}
                  isSelected={element.id === selectedElementId}
                  onSelect={onElementSelect}
                  onDelete={onElementDelete}
                  isEditorMode={isEditorMode}
                />
              );
            }

            // All other elements are draggable
            return (
              <Draggable
                key={element.id}
                draggableId={element.id}
                index={index}
              >
                {(provided, snapshot) => (
                  <CanvasElement
                    element={element}
                    path={[...path, element.id]}
                    dragProvided={provided}
                    isDragging={snapshot.isDragging}
                    isSelected={element.id === selectedElementId}
                    onSelect={onElementSelect}
                    onDelete={onElementDelete}
                    isEditorMode={isEditorMode}
                  />
                )}
              </Draggable>
            );
          })}

          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
};

/**
 * Individual element wrapper with drag handles and selection
 */
const CanvasElement = ({
  element,
  path,
  dragProvided,
  isDragging,
  isSelected,
  onSelect,
  isEditorMode,
  onDelete
}) => {
  const handleClick = (e) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(element.id, path);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(element.id, path);
    }
  };

  // Special handling for grid elements - use display: contents to maintain grid layout
  const isGridCell = element.type === 'grid-cell';
  const isGridContainer = element.type === 'grid-container';
  const isGridElement = isGridCell || isGridContainer;

  // Grid-container is not draggable, so dragProvided will be null
  const draggableProps = dragProvided ? {
    ref: dragProvided.innerRef,
    ...dragProvided.draggableProps
  } : {};

  return (
    <div
      {...draggableProps}
      className={`
        canvas-element-wrapper
        ${isGridCell ? 'grid-cell-wrapper' : ''}
        ${isGridContainer ? 'grid-container-wrapper' : ''}
        ${isDragging ? 'dragging' : ''}
        ${isSelected ? 'selected' : ''}
        ${isEditorMode ? 'editor-mode' : ''}
      `}
      onClick={handleClick}
    >
      {/* Drag handle - only visible in editor mode (not for grid elements) */}
      {isEditorMode && !isGridElement && dragProvided && (
        <div {...dragProvided.dragHandleProps} className="element-drag-handle">
          <span className="drag-icon"><GripVertical size={16} /></span>
        </div>
      )}

      {/* Element content */}
      <div className="element-content">
        <ElementRenderer
          element={element}
          path={path}
          onElementSelect={onSelect}
          selectedElementId={isSelected ? element.id : null}
          isEditorMode={isEditorMode}
          onElementDelete={onDelete}
        />
      </div>

      {/* Selection overlay with delete button - only in editor mode (not for grid elements) */}
      {isEditorMode && isSelected && !isGridElement && (
        <div className="element-selection-overlay">
          <div className="selection-label">
            {element.type}
          </div>
          <button
            className="element-delete-button"
            onClick={handleDelete}
            title="Delete element"
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Canvas;
